import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { fetchSession } from '../lib/admin-api'

/**
 * Whether the current visitor is the signed-in owner.
 *
 * Purely presentational — every admin capability is enforced server-side by
 * the session cookie. This only decides what the public pages should say, so
 * a stale or spoofed answer changes nothing but the wording.
 */
export function useIsAdmin(): boolean {
  const { data } = useQuery({
    queryKey: ['session'],
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
    // The public site works fine signed out; a failed check just means "no".
    retry: false,
  })

  return data?.authenticated ?? false
}

/**
 * A slim bar across the public site when signed in.
 *
 * Without it there is no way to tell an admin session from an anonymous one
 * while browsing the catalog — which matters, because the pages behave
 * differently: the owner is shown the keys rather than a way to ask for them.
 */
export function AdminBar() {
  const isAdmin = useIsAdmin()
  if (!isAdmin) return null

  // Opaque rather than blurred: this bar is on screen for the whole of every
  // scroll, and a backdrop-filter there is a layer the compositor has to
  // redraw the entire time.
  // Tinted with the accent rather than carrying a colour of its own. It used
  // to be a hand-picked navy — a shade of the old blue accent — so it stayed
  // blue when everything around it went green: the one strip on the page still
  // wearing the previous palette.
  return (
    <div className="sticky top-0 z-40 border-b border-accent/25 bg-[color-mix(in_oklab,var(--color-accent)_14%,var(--color-surface-0))]">
      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-3 px-4 py-1.5 sm:px-8">
        <span className="font-mono text-[11px] whitespace-nowrap tracking-wider text-accent-strong uppercase">
          {/* Wrapped onto two lines on a phone otherwise, doubling the height
              of a bar that is only meant to be a reminder. */}
          <span className="sm:hidden">Owner</span>
          <span className="hidden sm:inline">Signed in as owner</span>
        </span>
        <span className="hidden text-xs text-ink-muted sm:inline">viewing the public site</span>

        <div className="ml-auto flex items-center gap-3">
          <Link
            to="/admin/requests"
            className="text-xs text-ink-soft transition-colors hover:text-ink"
          >
            Requests
          </Link>
          <Link
            to="/admin"
            className="rounded-md bg-accent px-2.5 py-1 text-xs font-semibold text-surface-0 transition-colors hover:bg-accent-strong"
          >
            Admin
          </Link>
        </div>
      </div>
    </div>
  )
}
